import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';
import { listAdHocCharges, createAdHocCharge, cancelAdHocCharge } from '../../api/invoicing';
import { useAuth } from '../../auth/useAuth';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import type { AdHocCharge, CustomerSite, InvoiceFrequency } from '../../types/api';

function money(value: string | number) {
  return `£${Number(value).toFixed(2)}`;
}

function statusTone(status: AdHocCharge['status']): 'green' | 'amber' | 'slate' {
  if (status === 'ACTIVE') return 'green';
  if (status === 'CANCELLED') return 'amber';
  return 'slate';
}

// New ad-hoc contract modal - MSP Admin only (both the button that opens it
// and the underlying endpoint are gated). No catalog item involved at all -
// this is Perry's "sell them a contract for their Network Switches" case,
// a flat total spread evenly over a term.
function NewContractModal({ orgId, sites, open, onClose }: { orgId: string; sites: CustomerSite[]; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [siteId, setSiteId] = useState(sites[0]?.id ?? '');
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [termMonths, setTermMonths] = useState('36');
  const [frequency, setFrequency] = useState<InvoiceFrequency>('MONTHLY');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setSiteId(sites[0]?.id ?? '');
    setDescription('');
    setTotalAmount('');
    setCostAmount('');
    setTermMonths('36');
    setFrequency('MONTHLY');
    setError(null);
  }

  const mutation = useMutation({
    mutationFn: () =>
      createAdHocCharge(orgId, {
        siteId,
        description,
        totalAmount: Number(totalAmount),
        costAmount: costAmount ? Number(costAmount) : undefined,
        termMonths: Number(termMonths),
        frequency,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-hoc-charges', orgId] });
      showToast('Contract created.');
      reset();
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not create this contract.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!siteId) {
      setError('Choose a site for this contract.');
      return;
    }
    mutation.mutate();
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New contract"
      size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button form="new-contract-form" type="submit" isLoading={mutation.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id="new-contract-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Alert tone="info">
          For a charge that isn't tied to a shop item - e.g. "we now own and manage your Network Switches". The
          total is spread evenly across the term, prorated for the first charge if it starts mid-cycle.
        </Alert>
        <Select label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={sites.length === 0}>
          {sites.length === 0 && <option value="">No sites yet</option>}
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input
          label="Description"
          required
          autoFocus
          placeholder="Managed Network Switches"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Total contract value (£)"
            type="number"
            min="0.01"
            step="0.01"
            required
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
          />
          <Input
            label="Oakford's cost (£, optional)"
            type="number"
            min="0"
            step="0.01"
            hint="Internal only - never shown to the customer."
            value={costAmount}
            onChange={(e) => setCostAmount(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Term (months)"
            type="number"
            min="1"
            required
            value={termMonths}
            onChange={(e) => setTermMonths(e.target.value)}
          />
          <Select label="Billing frequency" value={frequency} onChange={(e) => setFrequency(e.target.value as InvoiceFrequency)}>
            <option value="MONTHLY">Monthly</option>
            <option value="ANNUAL">Annual</option>
          </Select>
        </div>
      </form>
    </Modal>
  );
}

// §113 - "ad-hoc" because it isn't tied to any shop order/catalog item, just
// an MSP-Admin-created contract spread over a term. Visible (read-only,
// cost hidden) to this customer's own Customer Admin too - the service
// strips costAmount server-side for anyone who isn't an MSP Admin, so this
// component doesn't need its own gating logic beyond hiding the column
// when it's simply absent from the data.
export function AdHocContractsSection({ orgId, sites }: { orgId: string; sites: CustomerSite[] }) {
  const { user } = useAuth();
  const isMspAdmin = !!user?.isMspAdmin;
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<AdHocCharge | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const chargesQuery = useQuery({ queryKey: ['ad-hoc-charges', orgId], queryFn: () => listAdHocCharges(orgId) });

  const cancelMutation = useMutation({
    mutationFn: () => cancelAdHocCharge(cancelTarget!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-hoc-charges', orgId] });
      showToast('Contract cancelled.');
      setCancelTarget(null);
    },
    onError: (err) => setCancelError(apiErrorMessage(err, 'Could not cancel this contract.')),
  });

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <span className="text-sm font-medium text-slate-600">
          {chargesQuery.data?.length ?? 0} contract{chargesQuery.data?.length === 1 ? '' : 's'}
        </span>
        {isMspAdmin && (
          <Button size="sm" onClick={() => setNewModalOpen(true)} disabled={sites.length === 0}>
            <Plus className="h-3.5 w-3.5" /> New contract
          </Button>
        )}
      </div>
      {chargesQuery.isLoading ? (
        <div className="py-14 text-center">
          <Spinner />
        </div>
      ) : !chargesQuery.data || chargesQuery.data.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No contracts yet"
          description="Create a contract for a charge that isn't tied to a shop item, e.g. a managed network switch estate."
        />
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th>Description</Th>
              <Th>Site</Th>
              <Th>Total</Th>
              <Th>Instalment</Th>
              <Th>Remaining</Th>
              <Th>Status</Th>
              <Th></Th>
            </tr>
          </Thead>
          <tbody>
            {chargesQuery.data.map((c) => (
              <Tr key={c.id}>
                <Td className="font-medium text-slate-900">{c.description}</Td>
                <Td>{c.site?.name ?? '-'}</Td>
                <Td>{money(c.totalAmount)}</Td>
                <Td>
                  {money(c.instalmentAmount)} / {c.frequency === 'ANNUAL' ? 'yr' : 'mo'}
                </Td>
                <Td>{c.remainingOccurrences}</Td>
                <Td>
                  <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                </Td>
                <Td className="text-right">
                  {isMspAdmin && c.status === 'ACTIVE' && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setCancelError(null);
                        setCancelTarget(c);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {isMspAdmin && <NewContractModal orgId={orgId} sites={sites} open={newModalOpen} onClose={() => setNewModalOpen(false)} />}

      <ConfirmModal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={() => cancelMutation.mutate()}
        title="Cancel contract"
        description={`${cancelTarget?.description} will stop being billed from the next invoicing run. Instalments already invoiced are not affected, and this cannot be undone.`}
        confirmLabel="Cancel contract"
        tone="danger"
        isLoading={cancelMutation.isPending}
        error={cancelError}
      />
    </Card>
  );
}
